import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Button, Box, Container, IconButton, Menu, MenuItem, Avatar } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchor);
  const settingsMenuOpen = Boolean(settingsMenuAnchor);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 15) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services/', label: 'Services' },
    { path: '/appointments/', label: 'Appointments' },
    { path: '/articles/', label: 'Articles' },
    { path: '/gallery/', label: 'Gallery' },
    { path: '/about/', label: 'About' },
    { path: '/contact/', label: 'Contact' },
  ];

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleSettingsMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: scrolled ? '1px solid rgba(53, 188, 179, 0.15)' : '1px solid rgba(53, 188, 179, 0.08)',
        boxShadow: scrolled ? '0 10px 30px -10px rgba(0, 0, 0, 0.05)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: scrolled ? '64px !important' : '76px !important',
            justifyContent: 'space-between',
            transition: 'min-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
            onClick={() => {
              if (location.pathname !== '/') {
                navigate('/');
              }
            }}
          >
            <Box
              component="img"
              src="/logo192.png"
              alt="Dynamic Dental Clinic Logo"
              title="Dynamic Dental Clinic Logo"
              loading="eager"
              sx={{
                width: 'auto',
                height: scrolled ? { xs: 40, sm: 48 } : { xs: 44, sm: 54 },
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '4px',
                border: '1px solid rgba(53, 188, 179, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 0.5,
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'flex-end',
              ml: 4,
            }}
          >
            {navItems.map((item) => {
              const cleanPath = (p: string) => p.endsWith('/') ? p : `${p}/`;
              const isActive = cleanPath(location.pathname) === cleanPath(item.path);
              return (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.3px',
                    padding: '8px 14px',
                    margin: '0 4px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    color: isActive ? '#2aa899' : '#475569',
                    backgroundColor: 'transparent',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: isActive ? '70%' : '0%',
                      height: '3px',
                      backgroundColor: '#35bcb3',
                      borderRadius: '2px',
                      transition: 'width 0.2s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#2aa899',
                      '&::before': {
                        width: '70%',
                      },
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}

            {user && (
              <IconButton
                onClick={handleSettingsMenuOpen}
                sx={{
                  color: '#475569',
                  borderRadius: '50%',
                  padding: '8px',
                  ml: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(53, 188, 179, 0.08)',
                    color: '#2aa899',
                    transform: 'rotate(30deg)',
                  },
                }}
              >
                <SettingsIcon sx={{ fontSize: '24px' }} />
              </IconButton>
            )}
          </Box>

          {/* Mobile Menu & Settings Icons */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5 }}>
            {user && (
              <IconButton
                onClick={handleSettingsMenuOpen}
                sx={{
                  color: '#475569',
                  borderRadius: '50%',
                  '&:hover': { color: '#2aa899' },
                }}
              >
                <SettingsIcon />
              </IconButton>
            )}
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: '#475569',
                borderRadius: '50%',
                '&:hover': { color: '#2aa899' },
              }}
            >
              <MenuIcon sx={{ fontSize: '28px' }} />
            </IconButton>
          </Box>

          {/* Mobile Navigation Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={mobileMenuOpen}
            onClose={handleMobileMenuClose}
            PaperProps={{
              sx: {
                width: '100%',
                maxWidth: '320px',
                mt: 1.5,
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(53, 188, 179, 0.1)',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(10px)',
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {navItems.map((item) => {
              const cleanPath = (p: string) => p.endsWith('/') ? p : `${p}/`;
              const isActive = cleanPath(location.pathname) === cleanPath(item.path);
              return (
                <MenuItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={handleMobileMenuClose}
                  sx={{
                    py: 1.8,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: isActive ? '#1e8a7f' : '#475569',
                    borderBottom: '1px solid rgba(53, 188, 179, 0.05)',
                    backgroundColor: isActive ? 'rgba(53, 188, 179, 0.06)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(53, 188, 179, 0.1)',
                      color: '#2aa899',
                    },
                  }}
                >
                  {item.label}
                </MenuItem>
              );
            })}
          </Menu>

          {/* Settings Menu (for both mobile and desktop) */}
          <Menu
            anchorEl={settingsMenuAnchor}
            open={settingsMenuOpen}
            onClose={handleSettingsMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(53, 188, 179, 0.1)',
                minWidth: '220px',
                backgroundColor: 'white',
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled sx={{ opacity: 1, py: 1.8, px: 2.5, borderBottom: '1px solid #f1f5f9' }}>
              <Avatar sx={{ mr: 2, bgcolor: '#35bcb3', width: 32, height: 32, fontSize: '0.9rem' }}>
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
              <Box>
                <Box sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{user?.name || 'User'}</Box>
                <Box sx={{ fontSize: '0.75rem', color: '#64748b' }}>{user?.email}</Box>
              </Box>
            </MenuItem>
            {user?.role === 'admin' && (
              <MenuItem
                component={Link}
                to="/admin/dashboard"
                onClick={handleSettingsMenuClose}
                sx={{ py: 1.5, px: 2.5, fontSize: '0.9rem', color: '#475569', '&:hover': { backgroundColor: 'rgba(53, 188, 179, 0.05)', color: '#2aa899' } }}
              >
                Admin Dashboard
              </MenuItem>
            )}
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 2.5,
                fontSize: '0.9rem',
                color: '#ef4444',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.05)' },
              }}
            >
              Logout
            </MenuItem>
          </Menu>

        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
